/**
 * Compact Decision-mode banner for the run workspace.
 * Makes PROVISIONAL / VERIFIED / etc. impossible to miss.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { UiDecisionMode } from './decisionModeFromWorkingSet';

interface DecisionModeBannerProps {
  mode: UiDecisionMode;
  /** Optional: chat may have overridden instance defaults this session. */
  sessionOverridesActive?: boolean;
  className?: string;
}

const MODE_STYLES: Record<UiDecisionMode, string> = {
  PROVISIONAL:
    'border-[var(--color-warning,var(--color-border))] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]',
  VERIFIED:
    'border-[var(--color-primary-bg)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]',
  EVIDENCE_ONLY:
    'border-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]',
};

export const DecisionModeBanner: React.FC<DecisionModeBannerProps> = ({
  mode,
  sessionOverridesActive = false,
  className = '',
}) => {
  const { t } = useLanguage();
  const titleKey = `aiStudio.workspace.decisionMode.${mode}.title`;
  const bodyKey = `aiStudio.workspace.decisionMode.${mode}.body`;

  return (
    <div
      role="status"
      className={`rounded border px-3 py-2 text-sm ${MODE_STYLES[mode]} ${className}`}
    >
      <p className="font-semibold tracking-wide">{t(titleKey)}</p>
      <p className="mt-0.5 text-[var(--color-surface-text-secondary)]">{t(bodyKey)}</p>
      {sessionOverridesActive ? (
        <p className="mt-1 text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.workspace.sessionOverrideHint')}
        </p>
      ) : null}
    </div>
  );
};
